import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { find, map, remove } from 'lodash'
import { produce } from 'immer'
import itemService from '../services/item.service'
import itemApi, { ItemData } from '../apis/item.api'
import { ActionName } from '../components/Actions'
import Heading from '../components/Heading'
import Row from '../components/Row/Row'
import TableHorizontal, {
  TableHorizontalData, TableHorizontalHeader
} from '../components/TableHorizontal'
import Button from '../components/Button'
import InputText from '../components/InputText'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'

export interface ItemMaterialInputData {
  item: ItemData
  quantity?: string
}

interface Props {
  addeds: ItemMaterialInputData[]
  onChange: (data: ItemMaterialInputData[]) => void
}

interface State {
  material: string
  selectedMaterial: ItemData | null
  quantity: string
  materials: ItemData[]
}

export default class ItemMaterialInput extends Component<Props, State> {
  initialState = {
    material: '',
    selectedMaterial: null,
    quantity: '',
    materials: []
  }

  constructor (props: Props) {
    super(props)
    this.state = this.initialState
    this.handleMaterialSearch = this.handleMaterialSearch.bind(this)
    this.handleMaterialSelect = this.handleMaterialSelect.bind(this)
    this.handleMaterialBlur = this.handleMaterialBlur.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleAdd = this.handleAdd.bind(this)
    this.handleActionsClick = this.handleActionsClick.bind(this)
  }

  get headers (): TableHorizontalHeader[] {
    return [
      { heading: i18n("Material item code"), path: 'item.code' },
      { heading: i18n("Quantity"), path: 'quantity' },
      { heading: i18n("Actions") }
    ]
  }

  get addedData (): TableHorizontalData<ItemMaterialInputData>[] {
    return map(this.props.addeds, added => {
      const id = added.item.id
      return { id, ...added, actions: ['delete'] }
    })
  }

  get materialOptions () {
    return itemService.searchOptions(this.state.materials)
  }

  handleMaterialSearch (name: string, term: string) {
    itemApi.search(term, materials => {
      if (!materials) return
      this.setState({ materials })
    })
  }

  handleMaterialSelect (name: string, option: SearchPickerOption) {
    const id = option.id as number
    const material = itemService.find(this.state.materials, id)
    if (material) {
      this.setState({ selectedMaterial: material })
    }
    this.setState({ materials: [] })
  }

  handleMaterialBlur (name: string) {
    this.setState({ materials: [] })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'material') {
      this.setState({ selectedMaterial: null, material: value })
    } else if (name === 'quantity') this.setState({ quantity: value })
  }

  pushAdded (
    item: ItemData,
    quantity: string
  ) {
    const addedData = { item, quantity }
    const exists = find(this.props.addeds, added => {
      return added.item.id === item.id
    })
    if (exists) {
      this.updateAdded(addedData)
    } else {
      const addeds = produce(this.props.addeds, addeds => {
        addeds.push(addedData)
      })
      this.props.onChange(addeds)
    }
  }

  updateAdded (updatedData: ItemMaterialInputData) {
    const addeds = produce(this.props.addeds, addeds => {
      remove(addeds, added => (added.item.id === updatedData.item.id))
      addeds.push(updatedData)
    })
    this.props.onChange(addeds)
  }

  removeAdded (id: number) {
    const addeds = produce(this.props.addeds, addeds => {
      remove(addeds, added => (added.item.id === id))
    })
    this.props.onChange(addeds)
  }

  clearFields () {
    this.setState(this.initialState)
  }

  handleAdd () {
    const { selectedMaterial, quantity } = this.state
    if (!selectedMaterial) return
    this.pushAdded(selectedMaterial, quantity)
    this.clearFields()
  }

  handleActionsClick (id: number, action: ActionName) {
    if (action === 'delete') this.removeAdded(id)
  }

  render () {
    return (
      <div>
        <Heading text={i18n("Material input list")} />
        <div className="mt-2">
          <TableHorizontal
            headers={this.headers}
            data={this.addedData}
            onActionClick={this.handleActionsClick} />
          <Row column={4}>
            <SearchPicker
              name="material"
              value={this.state.material}
              options={this.materialOptions}
              label={i18n("Material item code")}
              onSearch={this.handleMaterialSearch}
              onChange={this.handleInputChange}
              onSelect={this.handleMaterialSelect}
              onBlur={this.handleMaterialBlur} />
            <InputText
              name="quantity"
              value={this.state.quantity}
              label={i18n("Quantity")}
              onChange={this.handleInputChange} />
            <div className="flex items-end mb-4">
              <Button
                text={i18n("Add")}
                full={true}
                onClick={this.handleAdd} />
            </div>
          </Row>
        </div>
      </div>
    )
  }
}
